import React, { useEffect, useState, useMemo } from 'react';
import { Box, Container, Divider, Grid, List, ListItem, Typography } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getBlogs } from '../../apiCalls/urls/urlFunction';
import { titleToUrl } from '../../shared/extra';
import PageBreadcrumb from '../../components/other/PageBreadcrumb';
import PageLoading from "../../PageLoading";
import DefaultBlogImage1 from "../../assets/card-image/blog-detail.jpg";


const Index = ({ isMobile = false }) => {
    const [blogData, setBlogData] = useState([]);
    const [currentBlog, setCurrentBlog] = useState(null);
    const [loading, setLoading] = useState(true);

    const { title } = useParams();
    const location = useLocation();
    const canonicalUrl = useMemo(() => `https://www.99gift.in${location.pathname}`, [location.pathname]);

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                setLoading(true); // Set loading state
                const blogResponse = await getBlogs();
                const blogs = blogResponse?.data?.data || [];
                setBlogData(blogs);

                const matchedBlog = blogs.find(blog => titleToUrl(blog.title) === title);
                setCurrentBlog(matchedBlog);

            } catch (error) {
                console.error("An error occurred while loading the blog data. Please try again later.");
            } finally {
                setLoading(false); // Stop loading after fetching
            }
        };

        fetchBlogs();
    }, [title]);

    if (loading) {
        return <PageLoading />; // Display loading component during data fetching
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>
                    {
                    currentBlog?.['metaSEO']?.['metaTitle'] ?
                        (currentBlog?.['metaSEO']?.['metaTitle']) :
                        (currentBlog?.title)
                    }
                </title>

                <meta
                    property="og:title"
                    content={
                        currentBlog?.['metaSEO']?.['metaTitle'] ?
                        (currentBlog?.['metaSEO']?.['metaTitle']) :
                        (currentBlog?.title)
                    }
                />

                <meta
                    name="description"
                    content={
                        currentBlog?.['metaSEO']?.['metaDescription'] ?
                            (currentBlog?.['metaSEO']?.['metaDescription']) :
                            (currentBlog?.title)
                    }
                />

                <meta
                    name="og:description"
                    content={
                        currentBlog?.['metaSEO']?.['metaOGDescription'] ?
                            (currentBlog?.['metaSEO']?.['metaOGDescription']) :
                            (currentBlog?.title)
                    }
                />

                <meta property="og:url" content={canonicalUrl} />
                <link rel="canonical" href={canonicalUrl} />

                {
                    currentBlog?.['metaSchema']
                    &&
                    (
                        <script type="application/ld+json">
                            {currentBlog?.['metaSchema']}
                        </script>
                    )
                }
            </Helmet>


            <Container maxWidth="xl">
                {!isMobile && (
                    <PageBreadcrumb home="Home" other="Blog" currentProduct={currentBlog?.title} />
                )}
            </Container>

            <Container maxWidth="xl" sx={{ backgroundColor: "white.main", mb: { xs: 10 } }} disableGutters>
                <Grid container>
                    <Grid item xs={12} md={8} lg={9} mt={3}>
                        <Box sx={{ px: { xs: 1, sm: 1, lg: 2 } }}>
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                <Box
                                    component="img"
                                    src={currentBlog?.blogBannerImage || DefaultBlogImage1}
                                    alt={currentBlog?.title || "Blog Image"}
                                    loading="lazy"
                                    sx={{
                                        width: "auto",
                                        maxWidth: "100%",
                                        height: { xs: "180px", md: "270px", lg: "300px" },
                                        objectFit: "contain",
                                        borderRadius: { xs: 2, md: 3 },
                                        boxShadow: { xs: 0, md: 3 }
                                    }}
                                />
                            </Box>

                            <Typography
                                variant="h1"
                                sx={{
                                    fontSize: { xs: "1.3rem", lg: "2rem" },
                                    fontWeight: 400,
                                    color: "primary.main",
                                    mt: 2
                                }}
                            >
                                {currentBlog?.title}
                            </Typography>

                            <Box
                                dangerouslySetInnerHTML={{ __html: currentBlog?.content || '' }}
                                sx={{
                                    mt: 1,
                                    fontSize: { xs: "13.5px", lg: "15.5px" },
                                    lineHeight: { xs: "16px", lg: "24.5px" },
                                    color: "dark.main",
                                    fontWeight: 400,
                                    wordWrap: "break-word",
                                    overflowX: "auto",
                                    maxWidth: "100%"
                                }}
                            />
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4} lg={3} sx={{ mt: { xs: 3, lg: 1 } }}>
                        <Box sx={{
                            mt: { xs: 3, md: 0 },
                            backgroundColor: "white.main",
                            borderRadius: { xs: 0, lg: 3 },
                            overflow: "hidden",
                            boxShadow: { xs: 1, lg: 4 },
                            position: "sticky",
                            top: 25
                        }}>
                            <Typography sx={{
                                fontSize: { xs: "1.1rem", lg: "15.5px" },
                                fontWeight: 500,
                                color: "white.main",
                                backgroundColor: "primary.main",
                                px: 2,
                                py: 1
                            }}>
                                Recent Blogs
                            </Typography>

                            <List
                                sx={{
                                    width: '100%',
                                    maxWidth: '100%',
                                    bgcolor: 'background.paper',
                                    position: 'relative',
                                    pt: 0,
                                    '& ul': {
                                        padding: 0,
                                    },
                                    minHeight: '50vh',
                                    maxHeight: { xs: '300px', sm: '400px', lg: 480 },
                                    overflowY: { xs: 'scroll', sm: 'scroll', lg: 'auto' },
                                    // Hide scrollbar styles
                                    '&::-webkit-scrollbar': {
                                        display: 'none',
                                    },
                                    'msOverflowStyle': 'none',  // Hide scrollbar in IE and Edge
                                    'scrollbarWidth': 'none',     // Hide scrollbar in Firefox
                                }}
                            >
                                {blogData?.map((blog) => (
                                    <React.Fragment key={`blog-${blog?.id}`}>
                                        <ListItem sx={{
                                            "&:hover": { bgcolor: "lightblue", cursor: "pointer" }
                                        }}>
                                            
                                                <Typography sx={{
                                                    fontSize: { xs: ".8rem", lg: ".9rem" },
                                                    color: "textPrimary.main",
                                                }}>
                                                    {blog.title}
                                                </Typography>
                                            
                                        </ListItem>
                                            <Divider />
                                    </React.Fragment>
                                ))}
                            </List>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </React.Fragment>
    );
};

export default Index;